/**
 * Login Page Styles
 * 
 * Purpose:
 * Shared styles object for the LoginPage component. Contains all inline style definitions
 * used in the login form, including container, card, form fields, inputs, buttons, error states,
 * and trust cue. All style values are moved verbatim from LoginPage.jsx to preserve visual
 * behavior exactly.
 * 
 * High-level Workflow:
 * 1. Styles object is exported as a named export for use in LoginPage.jsx.
 * 2. Component imports styles and uses style keys directly (e.g., styles.container, styles.card).
 * 3. All style values, keys, and properties are preserved exactly as they existed in
 *    LoginPage.jsx before extraction.
 * 
 * Assumptions & Invariants:
 * - Values moved verbatim: All style values, keys, numeric values, colors, spacing, and layout
 *   properties are moved verbatim from LoginPage.jsx to preserve visual behavior.
 *   No style values were changed during extraction.
 * - Style key names: All style key names are preserved exactly (e.g., container, card, header,
 *   title, subtitle, form, field, label, input, inputDisabled, passwordLabelRow, forgotLink,
 *   passwordWrapper, passwordInput, passwordToggle, passwordToggleIcon, error, button,
 *   buttonDisabled, trustCue, trustIcon, trustText). Component references these keys by name.
 * - CSS variables: Styles use CSS custom properties (e.g., var(--spacing-4), var(--color-text-primary))
 *   which are defined in the global CSS/theme system. These variables are assumed to be available
 *   at runtime.
 * - No style changes: This file contains only the extracted styles object. No style values, keys,
 *   or properties were modified during extraction. This is a mechanical move only.
 * 
 * Dependencies:
 * - Internal:
 *   - None (pure style object, no imports)
 * - External:
 *   - None (pure JavaScript object)
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Initial extraction from LoginPage.jsx (Phase 3 refactoring). All values moved
 *   verbatim to preserve visual behavior exactly.
 */

export const styles = {
  container: {
    width: '100%',
    margin: '0 auto',
    padding: 'var(--spacing-4)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    padding: 'var(--spacing-8)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--elevation-3)',
    border: '1px solid var(--color-border-light, #e8e8ea)',
    width: '100%',
    maxWidth: '440px',
  },
  header: {
    textAlign: 'center',
    marginBottom: 'var(--spacing-8)',
  },
  title: {
    fontSize: 'var(--font-size-2xl)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    lineHeight: 'var(--line-height-tight-medium)',
    letterSpacing: 'var(--letter-spacing-tight-medium)',
    margin: 0,
    marginBottom: 'var(--spacing-2)',
  },
  subtitle: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    margin: 0,
    lineHeight: 'var(--line-height-relaxed)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-4)',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
  },
  label: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-1)',
  },
  input: {
    padding: 'var(--spacing-3) var(--spacing-4)',
    border: '1px solid var(--color-border-light, #e8e8ea)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-base)',
    fontFamily: 'var(--font-family-primary)',
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-bg-primary)',
    transition: 'border-color var(--transition-base), box-shadow var(--transition-base), background-color var(--transition-base)',
    outline: 'none',
    width: '100%',
  },
  inputDisabled: {
    backgroundColor: 'var(--color-bg-tertiary, #eceef0)',
    cursor: 'not-allowed',
    opacity: 0.7,
  },
  passwordLabelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 'var(--spacing-1)',
  },
  forgotLink: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-brand-primary, #0F4C75)',
    textDecoration: 'none',
    fontWeight: 'var(--font-weight-medium)',
    transition: 'color var(--transition-base)',
  },
  passwordWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  passwordInput: {
    paddingRight: '3rem', // Space for toggle button
  },
  passwordToggle: {
    position: 'absolute',
    right: 'var(--spacing-2)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 'var(--spacing-1)',
    color: 'var(--color-text-tertiary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color var(--transition-base), transform var(--transition-fast)',
    zIndex: 1,
    borderRadius: 'var(--radius-sm)',
  },
  passwordToggleIcon: {
    color: 'inherit',
    strokeWidth: 1.5,
  },
  error: {
    padding: 'var(--spacing-3)',
    backgroundColor: 'var(--color-semantic-error-bg, rgba(239, 68, 68, 0.1))',
    color: 'var(--color-semantic-error-text, #991b1b)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-sm)',
    border: '1px solid var(--color-semantic-error-base, #ef4444)',
    lineHeight: 'var(--line-height-normal)',
  },
  button: {
    width: '100%',
    padding: 'var(--spacing-3) var(--spacing-4)',
    backgroundColor: 'var(--color-brand-primary, #0F4C75)',
    color: 'var(--color-text-inverse, #ffffff)',
    border: '1px solid var(--color-brand-primary, #0F4C75)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    cursor: 'pointer',
    transition: 'background-color var(--transition-base), border-color var(--transition-base), transform var(--transition-fast)',
    outline: 'none',
    marginTop: 'var(--spacing-2)',
  },
  buttonDisabled: {
    backgroundColor: 'var(--color-text-disabled, #a3a3a3)',
    borderColor: 'var(--color-text-disabled, #a3a3a3)',
    cursor: 'not-allowed',
    transform: 'none',
  },
  trustCue: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-1)',
    marginTop: 'var(--spacing-4)',
    paddingTop: 'var(--spacing-4)',
    borderTop: '1px solid var(--color-border-light, #e8e8ea)',
  },
  trustIcon: {
    fontSize: 'var(--font-size-sm)',
    lineHeight: 1,
  },
  trustText: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-tertiary)',
    fontWeight: 'var(--font-weight-normal)',
  },
};

/** Mobile overrides so form fits in viewport without scroll (keyboard closed). */
const mobileOverrides = {
  container: {
    padding: 'var(--spacing-2)',
  },
  card: {
    padding: 'var(--spacing-4)',
  },
  header: {
    marginBottom: 'var(--spacing-4)',
  },
  title: {
    marginBottom: 'var(--spacing-1)',
  },
  form: {
    gap: 'var(--spacing-3)',
  },
  input: {
    minHeight: '44px',
    boxSizing: 'border-box',
  },
  button: {
    minHeight: '44px',
    marginTop: 'var(--spacing-1)',
  },
  trustCue: {
    marginTop: 'var(--spacing-2)',
    paddingTop: 'var(--spacing-2)',
  },
};

/**
 * Returns styles for LoginPage. When isMobile, merges base styles with mobile overrides.
 * @param {boolean} isMobile
 * @returns {typeof styles}
 */
export function getLoginPageStyles(isMobile) {
  if (!isMobile) return styles;
  return Object.fromEntries(
    Object.keys(styles).map((key) => [
      key,
      { ...styles[key], ...(mobileOverrides[key] || {}) },
    ])
  );
}
